import React, {useEffect, useMemo, useState} from 'react';
import {ActivityDetailResponse, ActivityProgressInfoResponse} from '../../type';
import globalStore from '@/services/global.state';
import {Button, Image} from 'antd-mobile';
import DetailNavTitle from '@/components/business/detail-nav-title';
import {errorLog, goBack, replace, replaceByUri} from '@/utils';
import {useTranslation} from 'react-i18next';
import styles from '../common.module.scss';
import {useLogin} from '@/utils/state-hooks';
import RewardsCard from './rewards-card';
import CumulateHeaderCard from './cumulate-header-card';
import dayjs from 'dayjs';
import {CountDown} from '../common';
import {useRoute} from '@react-navigation/native';
import {
  calculateCurrentCumulateBonus,
  isCurrentBonusActivityType,
} from './current-bonus.utils';
import {
  getAwardTableVariant,
  getRewardsSource,
  shouldShowRewards,
} from './rewards.utils';
import {getVipConfigListV2} from '@/common-pages/vip/vip.service';
import {getVipLevel} from './vip-level.utils';
import {getActivityInfo, getActivityProgress} from '../../service';

interface VipRechargeTemplateData {
  backgroundPic?: string;
  backgroundColor?: string;
  eventRule?: string;
  buttonLinear?: string;
  eventRewards?: string;
  redirectUrl?: string;
}

const normalizeActivityId = (value: unknown) =>
  value == null ? '' : String(value).trim();

const getCachedPromotionDetail = () => {
  const cachedValue = localStorage.getItem('promotion-detail-v2');
  if (!cachedValue) {
    return;
  }

  try {
    return JSON.parse(cachedValue) as ActivityDetailResponse;
  } catch {
    console.info('parse detail error');
  }
};

const getCachedPromotionProgress = () => {
  const cachedValue = localStorage.getItem('promotion-progress-v2');
  if (!cachedValue) {
    return;
  }

  try {
    return JSON.parse(cachedValue) as ActivityProgressInfoResponse;
  } catch {
    console.info('parse progress error');
  }
};

const VipRecharge = () => {
  const [data, setData] = useState<ActivityDetailResponse>();
  const [vipLevel, setVipLevel] = useState(0);
  const [progress, setProgress] = useState<ActivityProgressInfoResponse>();
  const login = useLogin();
  const route = useRoute();
  const activityId = normalizeActivityId(
    (route.params as {id?: number | string} | undefined)?.id,
  );
  useEffect(() => {
    if (!login) {
      setVipLevel(0);
      return;
    }
    getVipConfigListV2().then(res => setVipLevel(getVipLevel(res || [])));
  }, [login]);
  useEffect(() => {
    const cachedDetail = getCachedPromotionDetail();
    const cachedProgress = getCachedPromotionProgress();
    const cachedActivityId = normalizeActivityId(
      cachedDetail?.activityInfo?.activityId,
    );

    if (!activityId) {
      setData(cachedDetail);
      setProgress(cachedProgress);
      return;
    }

    if (cachedActivityId === activityId) {
      setData(cachedDetail);
      setProgress(undefined);
      return;
    }

    setData(undefined);
    setProgress(undefined);
  }, [activityId]);

  useEffect(() => {
    if (!activityId) {
      return;
    }

    const cachedActivityId = normalizeActivityId(
      getCachedPromotionDetail()?.activityInfo?.activityId,
    );
    const shouldShowLoading = cachedActivityId !== activityId;
    let active = true;

    shouldShowLoading && globalStore.globalLoading.next(true);
    getActivityInfo({
      activityId,
      language: globalStore.lang,
    })
      .then(res => {
        if (!active) {
          return;
        }
        setData(res);
        localStorage.setItem('promotion-detail-v2', JSON.stringify(res));
      })
      .catch(error => {
        if (!active) {
          return;
        }
        errorLog('getActivityInfo error', error);
      })
      .finally(() => {
        active && shouldShowLoading && globalStore.globalLoading.next(false);
      });

    return () => {
      active = false;
    };
  }, [activityId]);

  useEffect(() => {
    if (!activityId) {
      return;
    }

    if (!login) {
      setProgress(undefined);
      return;
    }

    let active = true;
    getActivityProgress(activityId)
      .then(res => {
        if (!active) {
          return;
        }
        setProgress(res);
        localStorage.setItem('promotion-progress-v2', JSON.stringify(res));
      })
      .catch(error => {
        if (!active) {
          return;
        }
        errorLog('getActivityProgress error', error);
      });

    return () => {
      active = false;
    };
  }, [activityId, login]);
  const templateData = useMemo<VipRechargeTemplateData>(() => {
    if (!data) {
      return {};
    }
    try {
      let json = data.templateVariableJson || '{}';
      while (typeof json === 'string') {
        json = JSON.parse(json);
      }
      return json;
    } catch (error) {
      console.info('parse templateVariableJson error');
      return {};
    }
  }, [data]);
  const calcActivityTime = useMemo(() => {
    return (
      dayjs(data?.activityInfo?.startTime).format('DD/MM YYYY') +
      ' - ' +
      dayjs(data?.activityInfo?.endTime).format('DD/MM YYYY')
    );
  }, [data]);
  const award = useMemo(() => {
    if (!progress) {
      return data?.awardList?.[0] || null;
    }
    const _awardList = Array.from(data?.awardList || []);
    _awardList.sort((a, b) => +(a.minAmount || 0) - +(b.minAmount || 0));
    if (progress.progressValue) {
      for (const _award of _awardList) {
        if (+(_award.minAmount || 0) > +(progress.progressValue || 0)) {
          return _award;
        }
      }
      return _awardList[_awardList.length - 1];
    }
    return data?.awardList?.[0] || null;
  }, [progress, data]);
  const isCumulate = useMemo(() => {
    return data?.activityInfo?.activityType?.includes('CUMULATE');
  }, [data]);
  const currentBonusAmount = useMemo(() => {
    if (!isCurrentBonusActivityType(data?.activityInfo?.activityType)) {
      return undefined;
    }
    return calculateCurrentCumulateBonus(
      +(progress?.progressValue || 0),
      data?.awardList || [],
    );
  }, [data, progress?.progressValue]);
  const handlePlay = () => {
    if (!login) {
      replace('Login');
    } else {
      if (data?.activityInfo?.templateKey?.split('_')[1] === 'RECHARGE') {
        // 参数id = -2
        replace('Recharge', {id: (route.params as any)?.id});
      } else if (
        data?.activityInfo?.templateKey?.split('_')[1] === 'WITHDRAW'
      ) {
        replace('Withdraw');
      } else if (data?.activityInfo?.templateKey?.split('_')[1] === 'BET') {
        replaceByUri(templateData.redirectUrl || 'route:Index?screen=Home');
      }
    }
  };

  const activityType = data?.activityInfo?.activityType;
  const rewardsSource = getRewardsSource({
    templateKey: data?.activityInfo?.templateKey,
    activityType,
  });
  const awardTableVariant = getAwardTableVariant(
    activityType,
    data?.activityInfo?.templateKey,
  );
  const hasRewards = shouldShowRewards(
    rewardsSource,
    templateData.eventRewards,
  );

  const {t} = useTranslation();
  return (
    <div className="flex flex-col w-auto h-screen overflow-hidden relative">
      <DetailNavTitle
        onBack={goBack}
        title={t('promotionsV2.details')}
        serverRight
        hideAmount
      />
      <div
        className="flex flex-col flex-1 overflow-hidden relative"
        style={{
          background: templateData.backgroundColor,
        }}>
        <Image
          className="absolute top-0 left-0 w-full"
          src={templateData.backgroundPic}
          fallback={
            <div
              style={{
                background: templateData.backgroundColor,
              }}
            />
          }
        />
        <div className="flex flex-col mt-[9.125rem] flex-1 z-[5] overflow-hidden gap-2">
          <div className="flex flex-row items-center justify-between px-5 py-2">
            <div className="flex flex-col text-white text-xs gap-1">
              <span>{t('promotionsV2.activityTime')}</span>
              <span className="font-bold">{calcActivityTime}</span>
            </div>
            <div className="flex flex-col text-white text-xs items-end gap-1">
              <span>{t('promotionsV2.remainTime')}</span>
              <CountDown
                endTime={data?.activityInfo?.endTime}
                onEnd={() => {}}
              />
            </div>
          </div>

          <div className="flex flex-col flex-1 overflow-auto">
            {!!login && !!award && !!isCumulate && (
              <div className="px-4 pb-2">
                <CumulateHeaderCard
                  vipLevel={vipLevel}
                  progressValue={+(progress?.progressValue || 0)}
                  currentBonusAmount={currentBonusAmount}
                  bonusResult={+(award.value || 0)}
                  bonusType={award?.valueType || ''}
                  // 只有当这个是累计次数时，才是这个值，非累计次数不生效
                  // count={+(progress?.progressValue || 0)}
                  totalProgress={+(award.minAmount || 0)}
                  templateKey={data?.activityInfo?.templateKey}
                  onPlay={handlePlay}
                />
              </div>
            )}
            {!!hasRewards &&
              (rewardsSource === 'awardList' ? (
                <RewardsCard
                  source="awardList"
                  awardList={data?.awardList || []}
                  variant={awardTableVariant}
                />
              ) : (
                <RewardsCard
                  source="template"
                  data={templateData.eventRewards || ''}
                />
              ))}
            <div className="mx-4 bg-white rounded-xl flex flex-col p-3 gap-3">
              <div className="flex flex-row items-center gap-2">
                <div className="w-1 h-3 bg-c1" />
                <div className="text-t1 text-sm font-bold capitalize">
                  {t('promotionsV2.rules')}
                </div>
              </div>
              <div
                className={`flex flex-col flex-1 gap-2 ${styles['event-rule']}`}
                dangerouslySetInnerHTML={{__html: templateData.eventRule || ''}}
              />
            </div>
            <div className="h-[5.75rem] flex-none" />
          </div>
          <div
            className="absolute bottom-0 left-0 right-0 pt-8 pb-3 px-5"
            style={{
              background: templateData.buttonLinear || undefined,
            }}>
            <Button
              className="rounded-full bg-c1 text-white text-base font-bold w-full h-12 flex items-center justify-center before:rounded-full uppercase"
              onClick={handlePlay}>
              {t('promotionsV2.joinNow')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VipRecharge;
